import React from 'react';
import { Exercise, RunningStats, WorkoutAnalysis, WorkoutScore } from '../api/client';
import { formatLongDate } from '../lib/date';
import { haptic } from '../lib/telegram';
import './WorkoutCard.css';

interface WorkoutCardProps {
  date: string;
  exercises: Exercise[];
  aiAnalysis?: WorkoutAnalysis | null;
  runningStats?: RunningStats | null;
  score?: WorkoutScore | null;
  onClick?: () => void;
}

const intensityClass = (intensity?: string): string => {
  if (!intensity) return '';
  const lower = intensity.toLowerCase();
  if (lower.includes('низк')) return 'intensity-low';
  if (lower.includes('сред') || lower.includes('умер')) return 'intensity-medium';
  if (lower.includes('высок') || lower.includes('интенс')) return 'intensity-high';
  return '';
};

const intensityLabel = (intensity?: string): string => {
  if (!intensity) return '';
  const lower = intensity.toLowerCase();
  if (lower.includes('низк')) return 'Низкая';
  if (lower.includes('сред') || lower.includes('умер')) return 'Средняя';
  if (lower.includes('высок') || lower.includes('интенс')) return 'Высокая';
  return intensity;
};

const formatPace = (seconds?: number) =>
  seconds == null ? '—' : `${Math.floor(seconds / 60)}′${String(seconds % 60).padStart(2, '0')}″`;

export const WorkoutCard: React.FC<WorkoutCardProps> = ({ date, exercises, aiAnalysis, runningStats, score, onClick }) => {
  const safeExercises = Array.isArray(exercises) ? exercises : [];

  const handle = () => {
    if (onClick) {
      haptic.light();
      onClick();
    }
  };

  return (
    <div
      className="workout-card"
      onClick={handle}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => { if (onClick && (e.key === 'Enter' || e.key === ' ')) handle(); }}
    >
      <div className="workout-card-header">
        <div><h3>{formatLongDate(date)}</h3><span className="workout-card-type">{runningStats ? (runningStats.activity_type || 'Пробежка') : `${safeExercises.length} упр.`}</span></div>
        {score && <div className="workout-score-badge"><strong>{score.total}</strong><span>/100</span></div>}
      </div>

      {aiAnalysis?.summary && (
        <p className="workout-card-summary">{aiAnalysis.summary}</p>
      )}

      {runningStats && (
        <div className="workout-running-row">
          <div><strong>{runningStats.distance_km?.toFixed(2) ?? '—'}</strong><span>км</span></div>
          <div><strong>{formatPace(runningStats.pace_avg_sec_per_km)}</strong><span>темп</span></div>
          <div><strong>{runningStats.hr_avg ?? '—'}</strong><span>пульс</span></div>
        </div>
      )}

      {!runningStats && <div className="workout-card-exercises">
        {safeExercises.slice(0, 3).map((exercise, idx) => (
          <div key={exercise.id ?? idx} className="exercise-item">
            <span className="exercise-name">{exercise.name}</span>
            {(exercise.sets || exercise.reps) && (
              <span className="exercise-details">
                {exercise.sets ? `${exercise.sets}×` : ''}
                {exercise.reps ?? ''}
                {exercise.weight ? ` · ${exercise.weight}` : ''}
              </span>
            )}
          </div>
        ))}
        {safeExercises.length > 3 && (
          <div className="exercise-item-more">
            +{safeExercises.length - 3} ещё
          </div>
        )}
      </div>}

      {aiAnalysis?.intensity && (
        <div className="workout-card-footer">
          {score?.provisional && <span className="score-provisional">предварительно</span>}
          <span className={`intensity-badge ${intensityClass(aiAnalysis.intensity)}`}>
            {intensityLabel(aiAnalysis.intensity)}
          </span>
        </div>
      )}
    </div>
  );
};
